import { on, createReducer } from '@ngrx/store';
import { ItemDetailActions, ItemDetailAsyncActions } from './item-detail.actions';
import { MenuBaseActions } from '../base/menu-base.actions';
import { ConsumersActions } from '../consumer/consumers.actions';
import { FeatureScopeActions } from '../feature-scope/feature-scope.actions';
import { LocationSetsActions } from '../location-sets/location-sets.actions';
import { LocationSetTypesActions } from '../location-set-types/location-set-types.actions';
import { SubmenusActions } from '../submenus/submenus.actions';
import {asyncActionState, AsyncActionState} from '@emm/core/util/async-action-state';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {withLoadables} from '@emm/core/util/ngrx.util';

export const itemDetailFeatureKey = 'itemDetail';

export interface ItemDetailState extends AsyncActionState {
    selectedItemDetail: ItemDetail | null;
    selectedSubmenu: string;
    selectedCascadeSubmenus: string[] | null;
    isCopied: boolean;
}

const initialState: ItemDetailState = {
    selectedItemDetail: null,
    selectedCascadeSubmenus: [],
    selectedSubmenu: null,
    isCopied: null,
    ...asyncActionState.initial
};

const itemDetailCreateReducer = createReducer(
    initialState.selectedItemDetail,

    on(ItemDetailActions.loadSuccess, (state, { item }) => ({
        ...item
    })),

    on(ItemDetailActions.addLocalAttribute, (state, { attributeIndex }) => ({
        ...state,
        unassigned: [
            ...state.unassigned.slice(0, attributeIndex),
            ...state.unassigned.slice(attributeIndex + 1)
        ],
        attributes: [{ ...state.unassigned[attributeIndex], isDirty: true }, ...state.attributes]
    })),
    on(ItemDetailActions.removeLocalAttribute, (state, { attributeIndex }) => ({
        ...state,
        unassigned: [state.attributes[attributeIndex], ...state.unassigned],
        attributes: [
            ...state.attributes.slice(0, attributeIndex),
            ...state.attributes.slice(attributeIndex + 1, state.attributes.length)
        ]
    }))

);

const { loadActions, successActions, errorActions } = ItemDetailAsyncActions;

export const itemDetailReducer = withLoadables<ItemDetailState>({
    loadActions,
    successActions,
    errorActions
})(
    initialState,

    on(
        MenuBaseActions.init,
        MenuBaseActions.changeEffectiveDate,
        ConsumersActions.deleteSuccess,
        ConsumersActions.reloadConsumers,
        ConsumersActions.select,
        FeatureScopeActions.select,
        LocationSetTypesActions.select,
        LocationSetsActions.select,
        ItemDetailActions.deleteSuccess,
        ItemDetailActions.pasteCascadeSuccess,
        ItemDetailActions.deleteCascadeSuccess,
        SubmenusActions.select,
        SubmenusActions.deselect,
        () => ({ ...initialState })
    ),

    on(ItemDetailActions.load, (state: ItemDetailState) => ({ ...state, selectedItemDetail: null })),

    on(
        ItemDetailActions.loadSuccess,
        ItemDetailActions.addLocalAttribute,
        ItemDetailActions.removeLocalAttribute,
        (state: ItemDetailState, action) => ({
            ...state,
            selectedItemDetail: itemDetailCreateReducer(state.selectedItemDetail, action),
            // forceOverrides: null,
        })
    ),

    on(
        ItemDetailActions.updateMenuItemSuccess,
        (state: ItemDetailState, action) => ({
            ...state,
            selectedItemDetail: action.item,
        })
    ),

    on(ItemDetailActions.selectCascadeSubmenus,
        ItemDetailActions.deselectCascadeSubmenus,
        (state: ItemDetailState, action) => ({
            ...state,
            selectedSubmenu: action.selectedSubmenuTag,
        })
    ),

    on(ItemDetailActions.selectCascadeSubmenusSuccess,
        (state: ItemDetailState, action) => ({
            ...state,
            selectedCascadeSubmenus: state.selectedCascadeSubmenus.includes(state.selectedSubmenu) || state.selectedSubmenu === null ?
                [...state.selectedCascadeSubmenus] : [...state.selectedCascadeSubmenus, state.selectedSubmenu],
        })
    ),

    on(ItemDetailActions.selectCascadeSubmenusFailure,
        (state: ItemDetailState, action) => ({
            ...state,
            error: 'Error: Failed to select a submenu'
        })
    ),

    on(ItemDetailActions.deselectCascadeSubmenusSuccess,
        (state: ItemDetailState, action) => ({
            ...state,
            selectedCascadeSubmenus: state.selectedCascadeSubmenus.filter(element => element !== state.selectedSubmenu)
        })
    ),

    on(ItemDetailActions.deselectCascadeSubmenusFailure,
        (state: ItemDetailState, action) => ({
            ...state,
            error: 'Error: Failed to deselect a submenu'
        })
    ),

    on(ItemDetailActions.resetCascadeSubmenus,
        (state: ItemDetailState, action) => ({
            ...state,
            selectedCascadeSubmenus: [],
        })
    ),
    on(ItemDetailActions.copy,
        (state: ItemDetailState, action) => ({
            ...state,
            fromPath: action.fromPath,
            isCopied: true,
        })),
    on(ItemDetailActions.paste,
        (state: ItemDetailState, action) => ({
            ...state,
            fromPath: action.fromPath,
            isCopied: false,
        }))
);
